import React from "react"
import {useAccessToken, useIsUnitReadonlyUser} from "../../../services/auth"
import {usePaging} from "../../../hooks/usePaging"
import {findOrgsWithPaginationMeta, getOrgName, Org} from "../../../resources/org"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"
import moment from "moment"
import PagingNavBar from "../../../components/PagingNavBar/PagingNavBar"
import {useNavigate} from "react-router-dom"
import TitleBar from "../../../components/TitleBar/TitleBar"
import {
	DataTable,
	DataTableBody,
	DataTableCard,
	DataTableCell,
	DataTableHead,
	DataTableRow,
	TablePending,
} from "../../../components/DataTable/DataTable"
import {Meta} from "../../../resources/common"
import Tag from "../../../components/Tag/Tag"
import {useModal} from "react-modal-hook"
import ManagedSolutionsConfigModal from "./ManagedSolutionsConfigModal"

function ManagedSolutionsOrgRow({org}: {org: Org}) {
	const navigate = useNavigate()

	return (
		<DataTableRow
			onClick={(e) => {
				e.preventDefault()
				navigate(`/admin/managed-solutions/${org.id}`)
			}}
		>
			<DataTableCell>{org.id}</DataTableCell>
			<DataTableCell className="org-name-cell">
				{getOrgName(org)}
				{!!org.relationships?.parentOrg && (
					<span style={{marginLeft: "8px"}}>
						<Tag size="small" variant="filled" theme="info" content="Managed Solutions" />
					</span>
				)}
				{org.attributes.tags?.orgBankingSetup === "ReadyToLaunchBanking" && (
					<span style={{marginLeft: "8px"}}>
						<Tag size="small" variant="filled" theme="success" content="Managed Solutions (Sandbox build path)" />
					</span>
				)}
			</DataTableCell>
			<DataTableCell>{moment(org.attributes.createdAt).format("L")}</DataTableCell>
		</DataTableRow>
	)
}

export default function ManagedSolutionsAdmin() {
	const PAGE_SIZE = 10000
	const accessToken = useAccessToken()
	const isUnitReadonly = useIsUnitReadonlyUser()

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		PAGE_SIZE,
		(offset, limit) => findOrgsWithPaginationMeta(accessToken, offset, limit, false, "", [], undefined),
		(x) => x.data.length,
		[]
	)

	const [showCreateConfigModal, hideCreateConfigModal] = useModal(() => (
		<ManagedSolutionsConfigModal
			mode="create"
			close={hideCreateConfigModal}
			onSuccess={() => {
				reset(PAGE_SIZE)
				hideCreateConfigModal()
			}}
		/>
	))

	return (
		<div className="managed-solutions-section">
			<DataTableCard className={"managed-solutions-orgs-card"}>
				<TitleBar title="Managed Solutions Configs">
					{!isUnitReadonly && (
						<button className="button is-success" onClick={showCreateConfigModal}>
							New App Config
						</button>
					)}
				</TitleBar>

				<AsyncResultComponent asyncResult={result} pendingComponent={<TablePending />}>
					{({value: orgs}) => {
						// Filter orgs to show those with relationships.parentOrg OR orgBankingSetup: ReadyToLaunchBanking tag
						const filteredOrgs = (orgs as {data: Org[]}).data.filter((org) => {
							const hasParentOrg = !!org.relationships?.parentOrg
							const hasManagedSolutionsTag = org.attributes.tags?.orgBankingSetup === "ReadyToLaunchBanking"
							return hasParentOrg || hasManagedSolutionsTag
						})

						const rows = filteredOrgs.map((org) => <ManagedSolutionsOrgRow key={org.id} org={org} />)

						return (
							<>
								<DataTable
									fullHeight={true}
									isEmpty={rows.length === 0}
									noContentText={"No Managed Solutions orgs found"}
								>
									<DataTableHead>
										<tr>
											<th>Org Id</th>
											<th>Name</th>
											<th>Created At</th>
										</tr>
									</DataTableHead>
									<DataTableBody>{rows}</DataTableBody>
								</DataTable>
								<PagingNavBar
									hasResults={hasResults}
									hasPrev={hasPrev}
									hasNext={hasNext}
									prev={prev}
									next={next}
									meta={(orgs as {meta: Meta}).meta}
								/>
							</>
						)
					}}
				</AsyncResultComponent>
			</DataTableCard>
		</div>
	)
}
